import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useCallback, useMemo } from 'react';
import { Button } from '@/components/button';
import { CircledIcon } from '@/components/circled-icon';
import { createStyles } from '@/contexts/theme-context';
import { InboundDetailHeaderMetadata } from './inbound-detail-header-metadata/inbound-detail-header-metadata';
import { OrderAgentWorkflowMode } from './types';
import {
  OrderInboundsAssignedEmployee,
  UnassignedEmployeeTrigger,
} from './order-inbounds-assigned-employee';
import { PROCESSED_STATUSES } from './utils';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateAssignedEmployee } from './queries';
import { View } from 'react-native';
import type { OrderInboundAssignedEmployee, OrderMetadata } from './types';

interface Props {
  action?: React.ReactNode;
  isProcessing?: boolean;
  isUnknownContact: boolean;
  leftSection?: React.ReactNode;
  onAddNewPress: () => void;
  orders: OrderMetadata[];
  workflowMode: OrderAgentWorkflowMode;
}

export const InboundDetailHeader: React.FC<Props> = ({
  action,
  isProcessing,
  isUnknownContact,
  leftSection,
  onAddNewPress,
  orders,
  workflowMode,
}) => {
  const [styles] = useStyles();
  const { isViewOnly } = useOrderInbounds();
  const { showErrorToast } = useToasts();

  const orderUUIDs = useMemo(() => {
    return orders
      .filter((order) => !PROCESSED_STATUSES.has(order.status))
      .map((order) => order.smartOrderUUID);
  }, [orders]);

  const updateAssignedEmployee = useUpdateAssignedEmployee();

  const onUpdateAssignedEmployee = useCallback(
    async (employeeUUID: string | null) => {
      try {
        await updateAssignedEmployee({
          employeeUUID,
          smartOrderUUIDs: orderUUIDs,
          supplierUUID: orders[0]?.supplierUUID ?? '',
        });
      } catch (err) {
        showErrorToast();
      }
    },
    [orderUUIDs, orders, showErrorToast, updateAssignedEmployee],
  );

  // We only need to look at the first order to get the assigned employee. Here are the assumptions for multiple orders:
  // - If two or more orders are merged and one of them has an assigned employee, then that employee is assigned to all
  //   of the orders.
  // - If two or more orders are merged and no assigned employee is found, then there is no assigned employee
  // - If two or more orders are merged but there are different assigned employees, then all of the orders are set to
  //   unassigned.
  //
  // These should be guaranteed when the data is already passed in to this component.
  // TODO: Maybe we add some reconciliation process here if the assumptions listed above are wrong?
  const assignedEmployee: OrderInboundAssignedEmployee | undefined =
    orders[0]?.assignedEmployee;

  return (
    <View style={styles.header}>
      <View style={styles.row}>
        {leftSection ? (
          <View style={styles.leftSectionContainer}>{leftSection}</View>
        ) : null}
        <InboundDetailHeaderMetadata
          isUnknownContact={isUnknownContact}
          orders={orders}
        />

        {workflowMode === OrderAgentWorkflowMode.Correction ? null : (
          <View style={styles.assignedEmployeeContainer}>
            <OrderInboundsAssignedEmployee
              assignedEmployee={assignedEmployee}
              onAssignEmployee={onUpdateAssignedEmployee}
              onUnassignEmployee={async () => {
                await onUpdateAssignedEmployee(null);
              }}
              renderUnassignedTrigger={(onPress) => (
                <UnassignedEmployeeTrigger
                  onPress={onPress}
                  style={styles.assignedEmployeeAssignToMeContainer}
                />
              )}
              restaurantName={orders[0]?.restaurantName ?? null}
              smartOrderUUID={orders[0]?.smartOrderUUID ?? ''}
              supplierUUID={orders[0]?.supplierUUID ?? ''}
            />
          </View>
        )}
      </View>
      {isProcessing ? null : (
        <View style={[styles.row, styles.action]}>
          {isViewOnly ||
          workflowMode === OrderAgentWorkflowMode.Correction ? null : (
            <Button
              disabled={isUnknownContact}
              leftElement={
                <CircledIcon
                  Icon={PlusIcon}
                  iconHeight={20}
                  iconWidth={20}
                  size="small"
                />
              }
              leftElementStyle={styles.leftElementStyle}
              onPress={onAddNewPress}
              testID="order-inbound-add-button"
              trackingData={{
                name: 'order_inbound_header.add_button',
                properties: {
                  orderUUIDs,
                },
              }}
              type="tertiary"
            >
              {i18n.t('add_item')}
            </Button>
          )}
          {action}
        </View>
      )}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  action: {
    alignItems: 'center',
    gap: SCREEN_PADDING_SMALL,
    justifyContent: 'flex-end',
  },
  assignedEmployeeAssignToMeContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
  },
  assignedEmployeeContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    marginLeft: 16,
  },
  header: {
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    flexDirection: 'row',
    height: 64,
    justifyContent: 'space-between',
    paddingRight: SCREEN_PADDING_SMALL,
  },
  leftElementStyle: {
    marginRight: 12,
  },
  leftSectionContainer: {
    justifyContent: 'center',
    marginLeft: SCREEN_PADDING_SMALL,
  },
  row: {
    flexDirection: 'row',
  },
}));
